import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import { sectorsService } from '../../services/api';
import { destinationAfterPlanCheck } from '../../services/planAccess';
import styles from './AuthPage.module.css';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    sectorId: ''
  });
  const [sectors, setSectors] = useState([]);
  const [sectorsError, setSectorsError] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadSectors = async () => {
      try {
        const data = await sectorsService.list();
        const list = Array.isArray(data) ? data : (data.data?.sectors || data.sectors || []);
        if (mounted) setSectors(list);
      } catch (error) {
        if (mounted) setSectorsError(error.message || 'Failed to load sectors');
      }
    };
    loadSectors();
    return () => { mounted = false; };
  }, []);

  const destinationFor = (role) => {
    const path = location.state?.from?.pathname;
    if (!path || path === '/login' || path === '/access-denied' || path === '/onboarding') {
      return '/erp';
    }
    const adminOnly = path === '/sectors' || path.startsWith('/admin');
    if (adminOnly && role !== 'admin') {
      return '/dashboard';
    }
    return path;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'confirmPassword' || name === 'password') {
      const newPassword = name === 'password' ? value : formData.password;
      const newConfirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword;
      setPasswordMatch(newPassword === newConfirmPassword);
    }
  };

  const goAfterAuth = async (role) => {
    const dest = destinationFor(role);
    if (role === 'admin') {
      navigate(dest, { replace: true });
      return;
    }
    navigate(await destinationAfterPlanCheck(dest), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (activeTab === 'register') {
        if (formData.password !== formData.confirmPassword) {
          showError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        if (!formData.sectorId) {
          showError('Please select a sector');
          setIsLoading(false);
          return;
        }
        
        const result = await register(
          formData.username,
          formData.fullName,
          formData.email,
          formData.password,
          formData.sectorId
        );
        if (result.success) {
          showSuccess('Registration successful! Welcome to Analytica X');
          navigate('/pricing', { replace: true });
        } else {
          showError(result.error);
        }
      } else {
        const result = await login(formData.username, formData.email, formData.password);        
        if (result.success) {
          showSuccess('Login successful! Welcome back');
          await goAfterAuth(result.user?.role);
        } else {
          showError(result.error,8000);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      showError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setFormData({
      username: '',
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      sectorId: ''
    });
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authContainer}>
        <div className={styles.authSidebar}>
          <div className={styles.sidebarContent}>
            <h2>Welcome to Analytica X</h2>
            <p>Access powerful analytics tools and transform your business data into actionable insights.</p>
            <ul className={styles.industryList}>
              <li className={styles.industryItem}>Education</li>
              <li className={styles.industryItem}>Automobile</li>
              <li className={styles.industryItem}>Real-Estate</li>
              <li className={styles.industryItem}>Healthcare</li>
              <li className={styles.industryItem}>Retail</li>
              <li className={styles.industryItem}>Hotels</li>
            </ul>
          </div>
        </div>

        <div className={styles.authMain}>
          <div className={styles.authHeader}>
            <button 
              className={styles.backButton} 
              onClick={() => navigate('/')}
            >
              ← Back to Home
            </button>
            <button 
              className={styles.backButton} 
              onClick={() => navigate('/report-issue')}
            >
              🐞 Report Issue
            </button>
          </div>

          <div className={styles.authForm}>
            <div className={styles.tabContainer}>
              <button
                className={`${styles.tabButton} ${activeTab === 'login' ? styles.active : ''}`}
                onClick={() => switchTab('login')}
              >
                Login
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'register' ? styles.active : ''}`}
                onClick={() => switchTab('register')}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="username">{activeTab === 'login' ? 'Username or Email' : 'Username'}</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  minLength={3}
                  placeholder={activeTab === 'login' ? "Enter your username or email" : "Enter your username"}
                />
              </div>

              {activeTab === 'register' && (
                <div className={styles.formGroup}>
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    minLength={2}
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              {activeTab === 'register' && (
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  placeholder="Enter your password"
                />
              </div>

              {activeTab === 'register' && (
                <div className={styles.formGroup}>
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                    placeholder="Confirm your password"
                  />
                  {formData.confirmPassword && (
                    <p className={`${styles.passwordMatchMessage} ${passwordMatch ? styles.success : styles.error}`}>
                      {passwordMatch ? 'Passwords matched' : 'Passwords not matched'}
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'register' && (
                <div className={styles.formGroup}>
                  <label htmlFor="sectorId">Sector</label>
                  <select
                    id="sectorId"
                    name="sectorId"
                    value={formData.sectorId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select your industry sector</option>
                    {sectors.map((sector) => (
                      <option key={sector.id || sector.sector_id} value={sector.id || sector.sector_id}>
                        {sector.name || sector.sector_name}
                      </option>
                    ))}
                  </select>
                  {sectorsError && (
                    <p className={`${styles.passwordMatchMessage} ${styles.error}`}>{sectorsError}</p>
                  )}
                </div>
              )}

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : activeTab === 'login' ? 'Login' : 'Register'}
              </button>
            </form>

            {activeTab === 'login' && (
              <>
                <div className={styles.oauthDivider}>
                  <span>or</span>
                </div>

                <GoogleLoginButton
                  className={styles.googleLoginButton}
                  onSuccess={(data) => {
                    showSuccess('Google login successful! Welcome back');
                    goAfterAuth(data?.user?.role);
                  }}
                  onError={() => {
                    showError('Google login failed. Please try again.');
                  }}
                />
              </>
            )}

            <div className={styles.authFooter}>
              <p>
                {activeTab === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button
                  className={styles.linkButton}
                  onClick={() => switchTab(activeTab === 'login' ? 'register' : 'login')}
                >
                  {activeTab === 'login' ? 'Register here' : 'Login here'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
